/**
 * <wispaire-deck>: the full-bleed card deck (snippets/deck.liquid). One native scroll-snap track.
 * Touch keeps native momentum: a fast flick can cross several cards and still snaps centred.
 * Trackpad, shift-wheel, mouse drag, keys and a click on a neighbour move one card per gesture
 * along a critically damped spring (SETTLE_MS) that any new input grabs mid-flight.
 * Each scroll frame writes every card's nearness to the centre (--deck-near, 0 to 1); deck.css
 * scales and lights cards from it, so the stage follows the finger. Reduced motion: moves land at once.
 */
import { defineElement, prefersReducedMotion } from '@wispaire/global';

export const SETTLE_MS = 260;
const DRAG_SLOP = 6;
const FLICK = 0.3; // px/ms: faster, the release direction beats the distance
const HOLD = 80; // ms still before release that cancel a flick
const WHEEL_STEP = 40; // px of wheel delta before a gesture commits
const WHEEL_QUIET = 140; // ms without wheel events that end a gesture
const ANNOUNCE_DELAY = 300;

/** The card the deck opens on: the middle one, so cards show on both sides. */
export const startIndex = (count) => Math.floor(count / 2);

const clamp = (index, count) => Math.min(count - 1, Math.max(0, index));

/** The card a scroll position rests on (card `i` centres at i × step). */
export function focusAt(scrollLeft, step, count) {
  return clamp(Math.round(scrollLeft / step), count);
}

/** How near card `index` is to the centre: 1 centred, 0 a card or more away. */
export function nearness(scrollLeft, step, index) {
  return Math.max(0, 1 - Math.abs(scrollLeft / step - index));
}

/** Release velocity (px/ms) of pointer samples; a hold before `upTime` counts as none. */
export function releaseVelocity(samples, upTime) {
  const [first, last] = [samples[0], samples.at(-1)];
  if (!last || last.t <= first.t || upTime - last.t > HOLD) return 0;
  return (last.x - first.x) / (last.t - first.t);
}

/** The card a key moves focus to, or null for keys the deck leaves alone. */
export function keyTarget(key, focus, count) {
  const targets = { ArrowLeft: focus - 1, ArrowRight: focus + 1, Home: 0, End: count - 1 };
  return key in targets ? clamp(targets[key], count) : null;
}

/** Cards a drag moves on release (-1, 0, 1; `dx`, `velocity` negative leftwards): a flick goes its way (against the drag: 0), a slow drag needs a quarter card. */
export function releaseStep(dx, velocity, width) {
  const drag = dx < 0 ? 1 : dx > 0 ? -1 : 0;
  if (Math.abs(velocity) > FLICK) {
    const flick = velocity < 0 ? 1 : -1;
    return drag && flick !== drag ? 0 : flick;
  }
  return Math.abs(dx) > width / 4 ? drag : 0;
}

/** Turns a stream of wheel deltas into one step (1 or -1) per gesture, else 0. */
export class WheelGesture {
  sum = 0;
  last = -Infinity;
  spent = false;

  take(delta, time) {
    if (time - this.last > WHEEL_QUIET) {
      this.sum = 0;
      this.spent = false;
    }
    this.last = time;
    if (this.spent) return 0;
    this.sum += delta;
    if (Math.abs(this.sum) < WHEEL_STEP) return 0;
    this.spent = true;
    return Math.sign(this.sum);
  }
}

/** Stops a glide where it is; snap takes over again. */
function stopGlide(track) {
  if (!track.glide) return;
  cancelAnimationFrame(track.glide);
  track.glide = 0;
  track.classList.remove('is-gliding');
}

/** Moves the track to `left`: at once under reduced motion, else on the spring. Snap is off meanwhile. */
export function glide(track, left, { reduced = prefersReducedMotion() } = {}) {
  stopGlide(track);
  if (reduced) {
    track.scrollLeft = left;
    return;
  }
  const from = track.scrollLeft;
  let start;
  track.classList.add('is-gliding');
  const tick = (now) => {
    start ??= now;
    const t = Math.min(1, (now - start) / SETTLE_MS);
    const a = 7.4 * t;
    track.scrollLeft = t === 1 ? left : from + (left - from) * (1 - (1 + a) * Math.exp(-a));
    if (t < 1) {
      track.glide = requestAnimationFrame(tick);
    } else {
      track.glide = 0;
      track.classList.remove('is-gliding');
    }
  };
  track.glide = requestAnimationFrame(tick);
}

/** Gives the deck's section the active slot's backdrop (data-tint); base.css eases the wash. */
export function bleed(host, slot) {
  if (host && slot?.dataset.tint) host.style.setProperty('--deck-tint', slot.dataset.tint);
}

const Base = globalThis.HTMLElement ?? class {};

export class Deck extends Base {
  connectedCallback() {
    const track = (this.track = this.querySelector('.deck__track'));
    this.slots = [...this.querySelectorAll('.deck__slot')];
    this.status = this.querySelector('[data-deck-status]');
    this.wheel = new WheelGesture();
    this.focus = -1;
    this.host = this.closest('[data-deck-tint]');
    this.measure();
    this.setFocus(startIndex(this.slots.length), false);
    track.scrollLeft = this.focus * this.step;
    this.stage();

    track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    track.addEventListener('pointermove', (event) => this.onPointerMove(event));
    track.addEventListener('pointerup', (event) => this.onPointerUp(event));
    track.addEventListener('pointercancel', (event) => this.onPointerUp(event));
    track.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('keydown', (event) => this.onKey(event));
    // Re-centres on a width change (a hidden Deck Mode first shown); height changes leave it be.
    this.resize = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === this.width) return;
      this.width = entry.contentRect.width;
      this.measure();
      stopGlide(track);
      track.scrollLeft = this.focus * this.step;
    });
    this.resize.observe(track);
    // The wash shows while the deck is in view.
    if (this.host) {
      this.seen = new IntersectionObserver(([e]) => this.host.toggleAttribute('data-tinted', e.isIntersecting), { threshold: 0.25 });
      this.seen.observe(track);
    }
  }

  disconnectedCallback() {
    this.resize?.disconnect();
    this.seen?.disconnect();
    this.host?.removeAttribute('data-tinted');
    stopGlide(this.track);
  }

  measure() {
    const [first, second] = this.slots;
    this.step = (second ? second.offsetLeft - first.offsetLeft : first?.offsetWidth) || 1;
  }

  stage() {
    const left = this.track.scrollLeft;
    this.slots.forEach((slot, i) => slot.style.setProperty('--deck-near', Math.round(nearness(left, this.step, i) * 1e3) / 1e3));
  }

  setFocus(index, announce = true) {
    if (index === this.focus) return;
    this.focus = index;
    this.slots.forEach((slot, i) => {
      const focused = i === index;
      slot.toggleAttribute('data-focused', focused);
      for (const control of slot.querySelectorAll('a, summary, button')) control.tabIndex = focused ? 0 : -1;
      if (!focused) slot.querySelector('details[open]')?.removeAttribute('open');
    });
    bleed(this.host, this.slots[index]);
    clearTimeout(this.announceTimer);
    if (announce && this.status) {
      this.announceTimer = setTimeout(() => {
        this.status.textContent = this.slots[this.focus].dataset.announce;
      }, ANNOUNCE_DELAY);
    }
  }

  goTo(index, { focusLink = false } = {}) {
    index = clamp(index, this.slots.length);
    this.setFocus(index);
    glide(this.track, index * this.step);
    if (focusLink) this.slots[index].querySelector('.card__link')?.focus({ preventScroll: true });
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.stage();
      // A glide or mouse drag knows where it lands: the cards it passes are not armed.
      if (!this.track.glide && !this.drag?.moved) this.setFocus(focusAt(this.track.scrollLeft, this.step, this.slots.length));
    });
  }

  onPointerDown(event) {
    stopGlide(this.track);
    if (event.pointerType === 'touch' || event.button !== 0) return;
    this.drag = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft, from: this.focus, moved: false, samples: [] };
  }

  onPointerMove(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved) {
      if (Math.abs(dx) < DRAG_SLOP) return;
      drag.moved = true;
      this.track.setPointerCapture(event.pointerId);
      this.track.classList.add('is-dragging');
    }
    this.track.scrollLeft = drag.left - dx;
    drag.samples.push({ x: event.clientX, t: event.timeStamp });
    if (drag.samples.length > 5) drag.samples.shift();
  }

  onPointerUp(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    this.drag = null;
    if (!drag.moved) return;
    this.track.classList.remove('is-dragging');
    this.dragged = true;
    setTimeout(() => (this.dragged = false));
    const velocity = releaseVelocity(drag.samples, event.timeStamp);
    this.goTo(drag.from + releaseStep(event.clientX - drag.x, velocity, this.step));
  }

  onWheel(event) {
    // Vertical wheels scroll the page; horizontal swipes and shift-wheel move the deck.
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
    if (event.ctrlKey || !delta) return;
    event.preventDefault();
    const step = this.wheel.take(delta, event.timeStamp);
    if (step) this.goTo(this.focus + step);
  }

  onClick(event) {
    if (this.dragged) {
      event.preventDefault();
      return;
    }
    const index = this.slots.indexOf(event.target.closest('.deck__slot'));
    if (index < 0 || index === this.focus) return;
    event.preventDefault();
    this.goTo(index);
  }

  onKey(event) {
    if (event.target.closest('details[open]')) return;
    const index = keyTarget(event.key, this.focus, this.slots.length);
    if (index === null) return;
    event.preventDefault();
    this.goTo(index, { focusLink: true });
  }
}

defineElement('wispaire-deck', Deck);
